import React, { useState } from 'react';

/**
 * Universal Avatar Component (Gmail-style)
 * Displays image or falls back to colored initials circle
 * Used for products, customers, staff, orders, etc.
 * 
 * @param {string} name - Entity name
 * @param {string} image - Image URL (optional)
 * @param {string} size - Size variant: 'sm' (32px), 'md' (40px), 'lg' (48px), 'xl' (56px)
 * @param {string} shape - Shape: 'circle' or 'rounded' (default: circle)
 * @param {string} className - Additional CSS classes
 */
const Avatar = ({
    name = '',
    image = null,
    size = 'md',
    shape = 'circle',
    className = ''
}) => {
    const [imageError, setImageError] = useState(false);

    // Extract initials from name (first letter of each word, max 2)
    const getInitials = (name) => {
        if (!name) return '?';

        const words = name.trim().split(/\s+/);
        if (words.length === 1) {
            // Single word: take first 2 characters
            return words[0].substring(0, 2).toUpperCase();
        }
        // Multiple words: take first letter of first 2 words
        return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
    };

    // Generate consistent color based on name hash
    const getColorFromName = (name) => {
        if (!name) return 'bg-gray-400';

        const colors = [
            'bg-blue-500',
            'bg-purple-500',
            'bg-pink-500',
            'bg-red-500',
            'bg-orange-500',
            'bg-amber-500',
            'bg-green-500',
            'bg-teal-500',
            'bg-cyan-500',
            'bg-indigo-500',
        ];

        // Simple hash function for consistent color
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        return colors[Math.abs(hash) % colors.length];
    };

    // Size configurations
    const sizeClasses = {
        xs: 'w-6 h-6 text-[10px]',
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
        xl: 'w-14 h-14 text-lg'
    };

    // Shape classes
    const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

    const shouldShowInitials = !image || imageError;

    if (shouldShowInitials) {
        return (
            <div
                className={`${sizeClasses[size]} ${getColorFromName(name)} ${shapeClass} flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0 ${className}`}
                title={name}
            >
                {getInitials(name)}
            </div>
        );
    }

    return (
        <img
            src={image}
            alt={name}
            onError={() => setImageError(true)}
            className={`${sizeClasses[size]} ${shapeClass} object-cover shadow-sm flex-shrink-0 ${className}`}
            title={name}
        />
    );
};

export default Avatar;
